import { Injectable } from '@angular/core';
import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Observable, Subscription } from 'rxjs';

const MAX_REQUESTS_PER_SECOND = 10;
const WINDOW_MS = 1000;

interface QueuedRequest {
  execute: () => void;
  unsubscribed: boolean;
  innerSub: Subscription | null;
}

@Injectable()
export class RateLimitInterceptor implements HttpInterceptor {
  private readonly requestTimestamps: number[] = [];
  private readonly queue: QueuedRequest[] = [];
  private drainTimer: ReturnType<typeof setTimeout> | null = null;
  private draining = false;

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    return new Observable<HttpEvent<any>>((subscriber) => {
      const queued: QueuedRequest = {
        unsubscribed: false,
        innerSub: null,
        execute: () => {
          if (queued.unsubscribed) {
            return;
          }
          queued.innerSub = next.handle(request).subscribe({
            next: (event) => subscriber.next(event),
            error: (err) => subscriber.error(err),
            complete: () => subscriber.complete(),
          });
        },
      };

      this.queue.push(queued);
      this.drainQueue();

      if (this.queue.indexOf(queued) !== -1) {
        console.log(
          '[RateLimitInterceptor] Request queued:',
          request.method,
          request.url
        );
      }

      return () => {
        queued.unsubscribed = true;
        const index = this.queue.indexOf(queued);
        if (index !== -1) {
          this.queue.splice(index, 1);
        }
        // Cancel the in-flight HTTP request if it has already been dispatched.
        queued.innerSub?.unsubscribe();
        queued.innerSub = null;
      };
    });
  }

  private drainQueue(): void {
    // Guard against synchronous re-entry from a downstream handler
    // (e.g. a test mock or an interceptor that emits synchronously and
    // triggers another HTTP call from inside the subscribe callback).
    if (this.draining) {
      return;
    }
    this.draining = true;
    try {
      this.pruneTimestamps();

      while (
        this.queue.length > 0 &&
        this.requestTimestamps.length < MAX_REQUESTS_PER_SECOND
      ) {
        const item = this.queue.shift();
        if (!item || item.unsubscribed) {
          continue;
        }
        this.requestTimestamps.push(Date.now());
        item.execute();
      }

      if (this.queue.length > 0 && this.drainTimer === null) {
        const waitMs = this.getMillisUntilNextSlot();
        this.drainTimer = setTimeout(() => {
          this.drainTimer = null;
          this.drainQueue();
        }, waitMs);
      }
    } finally {
      this.draining = false;
    }
  }

  private pruneTimestamps(): void {
    const cutoff = Date.now() - WINDOW_MS;
    while (
      this.requestTimestamps.length > 0 &&
      this.requestTimestamps[0] <= cutoff
    ) {
      this.requestTimestamps.shift();
    }
  }

  private getMillisUntilNextSlot(): number {
    if (this.requestTimestamps.length === 0) {
      return 0;
    }
    const oldest = this.requestTimestamps[0];
    return Math.max(1, oldest + WINDOW_MS - Date.now());
  }
}
